import { applyNewPriceToPhases } from '@sequencehq/utils'
import { match } from 'ts-pattern'
import { NEW_DISCOUNT_PREFIX } from 'Cube/domain/cube.constants'
import {
  AvailableFeaturesForPhase,
  CubeReducerState,
  Discount,
  Phase,
  Price,
  RecursivePartial,
  ResolvedPhase
} from 'Cube/domain/cube.domain.types'

/**
 * Why a phase cannot take a copy of a price, shown on the phase in the copy
 * sub-menu. A phase already holding the price reports on the discount, which
 * is all that is left to copy.
 */
export const COPY_TO_PHASE_DISABLED_REASONS = {
  priceAlreadyInPhase: 'This phase already uses this price',
  priceCannotBeChanged: "This phase's price for the product cannot be changed",
  productCannotBeAdded: 'A product cannot be added to this phase',
  wouldInvalidateScopedEntities:
    'A discount or minimum in this phase would no longer be valid',
  discountBlockedByGlobalDiscount: 'This phase already has a global discount',
  discountCoversOtherPrices:
    "This phase's discount for the price also covers other prices",
  discountCannotBeEdited: "This phase's discount cannot be edited",
  discountCannotBeAdded: 'A discount cannot be added to this phase'
} as const

export type CopyTargetPhase = {
  id: Phase['id']
  label: string
  /** Set when the phase is offered but cannot take the copy. */
  disabledReason?: string
}

export type CopyToPhaseResult = {
  update: RecursivePartial<CubeReducerState['data']>
  copiedEntityIds: string[]
}

export type CopyContext = {
  orderedPhases: ResolvedPhase[]
  availableFeatures: Record<Phase['id'], AvailableFeaturesForPhase>
  sourcePhase: ResolvedPhase
}

/** Phases are often unnamed, in which case they are known by their position. */
export const getPhaseLabel = (
  phase: Pick<ResolvedPhase, 'name'>,
  index: number
): string => phase.name || `Phase ${index + 1}`

/**
 * A price can be copied to a phase that could have gained it through its own
 * editors: overwriting the phase's price for the product, or adding a new one.
 *
 * Every other phase is offered, so that a phase which cannot take the copy can
 * say why rather than quietly going missing.
 */
export const getPriceCopyTargets = (
  ctx: CopyContext & { price: Price }
): CopyTargetPhase[] =>
  ctx.orderedPhases.reduce<CopyTargetPhase[]>((targets, phase, index) => {
    const availableFeatures = ctx.availableFeatures[phase.id]

    if (phase.id === ctx.sourcePhase.id || !availableFeatures) {
      return targets
    }

    const disabledReason = getPriceCopyDisabledReason({
      price: ctx.price,
      sourcePhase: ctx.sourcePhase,
      targetPhase: phase,
      availableFeatures
    })

    return [
      ...targets,
      {
        id: phase.id,
        label: getPhaseLabel(phase, index),
        ...(disabledReason ? { disabledReason } : {})
      }
    ]
  }, [])

/**
 * Prices are shared between phases, so a copy references the same price and
 * repoints everything in the destination scoped to the price it replaces. The
 * price's own discount comes with it where the destination can take one.
 */
export const getPriceCopyUpdate = (args: {
  price: Price
  sourcePhase: ResolvedPhase
  targetPhase: ResolvedPhase
  availableFeatures: AvailableFeaturesForPhase
}): CopyToPhaseResult => {
  // Only the destination is passed, so the update stops there.
  const updates = applyNewPriceToPhases({
    newPrice: args.price,
    phaseId: args.targetPhase.id,
    phases: [args.targetPhase]
  })

  const copiedDiscount = match(getDiscountCopy(args))
    .with({ outcome: 'apply' }, ({ discount }) => discount)
    .with(
      { outcome: 'blocked' },
      { outcome: 'none' },
      { outcome: 'unchanged' },
      () => undefined
    )
    .exhaustive()

  const copiedCreditGrants = getRepointedCreditGrants(args)

  return {
    update: {
      phases: {
        [args.targetPhase.id]: {
          priceIds:
            updates.updatedPhases[args.targetPhase.id]?.priceIds ??
            args.targetPhase.prices.map(price => price.id),
          phasePriceMetadata: getCopiedPhasePriceMetadata(args),
          ...(copiedDiscount
            ? {
                discountIds: [
                  ...new Set([
                    ...args.targetPhase.discounts.map(discount => discount.id),
                    copiedDiscount.id
                  ])
                ]
              }
            : {})
        }
      },
      discounts: {
        ...updates.updatedDiscounts,
        ...(copiedDiscount ? { [copiedDiscount.id]: copiedDiscount } : {})
      },
      minimums: updates.updatedMinimums,
      creditGrants: copiedCreditGrants
    },
    /**
     * The copied price keeps its id, so the source row flashes too. Flashing
     * is not scoped by phase, and the highlight is what expands the
     * destination phase.
     */
    copiedEntityIds: [
      args.price.id,
      ...Object.keys(updates.updatedDiscounts),
      ...Object.keys(updates.updatedMinimums),
      ...Object.keys(copiedCreditGrants),
      ...(copiedDiscount ? [copiedDiscount.id] : [])
    ]
  }
}

const getPriceCopyDisabledReason = (args: {
  price: Price
  sourcePhase: ResolvedPhase
  targetPhase: ResolvedPhase
  availableFeatures: AvailableFeaturesForPhase
}): string | undefined => {
  const existingPrice = findPriceForProduct(args.targetPhase, args.price)

  /**
   * A phase already holding the price can still take the price's discount, so
   * it is offered when that discount would land and reports on the discount,
   * not the price, when it would not.
   */
  if (existingPrice?.id === args.price.id) {
    return match(getDiscountCopy(args))
      .with({ outcome: 'apply' }, () => undefined)
      .with({ outcome: 'blocked' }, ({ reason }) => reason)
      .with(
        { outcome: 'none' },
        { outcome: 'unchanged' },
        () => COPY_TO_PHASE_DISABLED_REASONS.priceAlreadyInPhase
      )
      .exhaustive()
  }

  if (
    existingPrice &&
    !args.availableFeatures.products[args.price.productId]?.edit.available
      .enabled
  ) {
    return COPY_TO_PHASE_DISABLED_REASONS.priceCannotBeChanged
  }

  if (
    !existingPrice &&
    !args.availableFeatures.phase.product.add.available.enabled
  ) {
    return COPY_TO_PHASE_DISABLED_REASONS.productCannotBeAdded
  }

  if (
    !keepsScopedEntitiesValid({
      phase: args.targetPhase,
      price: args.price,
      existingPrice
    })
  ) {
    return COPY_TO_PHASE_DISABLED_REASONS.wouldInvalidateScopedEntities
  }

  return undefined
}

/**
 * The copy always scopes the discount to the copied price alone, so it can be
 * carried to a phase that could discount that price itself.
 *
 *   none      -> the price has no discount of its own to bring
 *   unchanged -> the destination already discounts the price the same way
 *   blocked   -> the destination cannot take it; the price copies without it
 *   apply     -> the discount to write into the destination
 */
type DiscountCopy =
  | { outcome: 'none' }
  | { outcome: 'unchanged' }
  | { outcome: 'blocked'; reason: string }
  | { outcome: 'apply'; discount: Discount }

const getDiscountCopy = (args: {
  price: Price
  sourcePhase: ResolvedPhase
  targetPhase: ResolvedPhase
  availableFeatures: AvailableFeaturesForPhase
}): DiscountCopy => {
  const sourceDiscount = findPriceDiscount(args.sourcePhase, args.price.id)

  if (!sourceDiscount) {
    return { outcome: 'none' }
  }

  // A global discount in the destination already covers every price in it.
  if (args.targetPhase.globalDiscount) {
    return {
      outcome: 'blocked',
      reason: COPY_TO_PHASE_DISABLED_REASONS.discountBlockedByGlobalDiscount
    }
  }

  const discountToReplace = findDiscountToReplace(args)

  if (discountToReplace) {
    return getDiscountReplacement({
      ...args,
      sourceDiscount,
      discountToReplace
    })
  }

  if (!args.availableFeatures.phase.discount.add.available.enabled) {
    return {
      outcome: 'blocked',
      reason: COPY_TO_PHASE_DISABLED_REASONS.discountCannotBeAdded
    }
  }

  return {
    outcome: 'apply',
    discount: {
      ...sourceDiscount,
      id: `${NEW_DISCOUNT_PREFIX}${crypto.randomUUID()}`,
      priceIds: [args.price.id]
    }
  }
}

/**
 * The destination's discount for the price it is about to lose ends up on the
 * copied price, so that is the one a copy would replace.
 */
const findDiscountToReplace = (args: {
  price: Price
  targetPhase: ResolvedPhase
}) => {
  const replacedPrice = findPriceForProduct(args.targetPhase, args.price)

  return args.targetPhase.discounts.find(
    discount =>
      !isAGlobalDiscount(discount) &&
      (discount.priceIds.includes(args.price.id) ||
        Boolean(replacedPrice && discount.priceIds.includes(replacedPrice.id)))
  )
}

/** Whether the copy can take over the destination's discount for the price. */
const getDiscountReplacement = (args: {
  price: Price
  sourceDiscount: Discount
  discountToReplace: Discount
  availableFeatures: AvailableFeaturesForPhase
}): DiscountCopy => {
  if (args.discountToReplace.priceIds.length > 1) {
    return {
      outcome: 'blocked',
      reason: COPY_TO_PHASE_DISABLED_REASONS.discountCoversOtherPrices
    }
  }

  if (discountsHaveTheSameEffect(args.discountToReplace, args.sourceDiscount)) {
    return { outcome: 'unchanged' }
  }

  if (
    !args.availableFeatures.products[args.price.productId]?.editDiscount
      .available.enabled
  ) {
    return {
      outcome: 'blocked',
      reason: COPY_TO_PHASE_DISABLED_REASONS.discountCannotBeEdited
    }
  }

  return {
    outcome: 'apply',
    discount: {
      ...args.sourceDiscount,
      id: args.discountToReplace.id,
      priceIds: [args.price.id]
    }
  }
}

/** Two discounts a customer could not tell apart, however they are scoped. */
const discountsHaveTheSameEffect = (
  discount: Discount,
  otherDiscount: Discount
) =>
  discount.discountCalculationType === otherDiscount.discountCalculationType &&
  discount.amount === otherDiscount.amount &&
  discount.seatDiscountType === otherDiscount.seatDiscountType &&
  discount.message === otherDiscount.message

/**
 * Cube drops a discount or minimum whose prices stop sharing one recurring
 * frequency, so a copied price must not invalidate what the phase already has.
 */
const keepsScopedEntitiesValid = (args: {
  phase: ResolvedPhase
  price: Price
  existingPrice?: Price
}) => {
  const pricesAfterCopy = args.existingPrice
    ? args.phase.prices.map(price =>
        price.id === args.existingPrice?.id ? args.price : price
      )
    : [...args.phase.prices, args.price]

  const resolveScope = (priceIds: string[]) =>
    priceIds
      .map(priceId =>
        priceId === args.existingPrice?.id
          ? args.price
          : pricesAfterCopy.find(price => price.id === priceId)
      )
      .filter((price): price is Price => Boolean(price))

  const discountsStayValid = args.phase.discounts.every(
    discount =>
      getRecurringFrequencies(
        isAGlobalDiscount(discount)
          ? pricesAfterCopy
          : resolveScope(discount.priceIds)
      ).length <= 1
  )

  const minimumsStayValid = args.phase.minimums.every(
    minimum =>
      getRecurringFrequencies(
        getUsagePrices(
          minimum.scope.target === 'allUsage'
            ? pricesAfterCopy
            : resolveScope(minimum.scope.priceIds)
        )
      ).length === 1
  )

  return discountsStayValid && minimumsStayValid
}

/**
 * An ARR setting belongs to a phase and price, so the destination's setting for
 * the price it loses follows the copy, and the source's setting wins.
 */
const getCopiedPhasePriceMetadata = (args: {
  price: Price
  sourcePhase: ResolvedPhase
  targetPhase: ResolvedPhase
}) => {
  const replacedPrice = findPriceForProduct(args.targetPhase, args.price)
  const metadata = args.targetPhase.phasePriceMetadata.map(entry =>
    entry.priceId === replacedPrice?.id
      ? { ...entry, priceId: args.price.id }
      : entry
  )
  const sourceMetadata = args.sourcePhase.phasePriceMetadata.find(
    entry => entry.priceId === args.price.id
  )

  if (!sourceMetadata) {
    return metadata
  }

  return [
    ...metadata.filter(entry => entry.priceId !== args.price.id),
    sourceMetadata
  ]
}

/**
 * A grant scoped to the price the destination loses would be cleaned away, so
 * it follows the copied price instead.
 */
const getRepointedCreditGrants = (args: {
  price: Price
  targetPhase: ResolvedPhase
}): Record<string, { priceIds: string[] }> => {
  const replacedPrice = findPriceForProduct(args.targetPhase, args.price)

  if (!replacedPrice || replacedPrice.id === args.price.id) {
    return {}
  }

  return args.targetPhase.creditGrants
    .filter(grant => grant.priceIds.includes(replacedPrice.id))
    .reduce<Record<string, { priceIds: string[] }>>(
      (grants, grant) => ({
        ...grants,
        [grant.id]: {
          priceIds: [
            ...grant.priceIds.filter(priceId => priceId !== replacedPrice.id),
            args.price.id
          ]
        }
      }),
      {}
    )
}

const findPriceForProduct = (phase: ResolvedPhase, price: Price) =>
  phase.prices.find(phasePrice => phasePrice.productId === price.productId)

const findPriceDiscount = (phase: ResolvedPhase, priceId: Price['id']) =>
  phase.discounts.find(
    discount =>
      !isAGlobalDiscount(discount) && discount.priceIds.includes(priceId)
  )

const isAGlobalDiscount = (discount: Discount) =>
  discount.applyToAllPrices || discount.priceIds.length === 0

const getUsagePrices = (prices: Price[]) =>
  prices.filter(price => 'usageMetricId' in price.structure)

const getRecurringFrequencies = (prices: Price[]) => [
  ...new Set(
    prices
      .map(price => price.billingFrequency)
      .filter(billingFrequency => billingFrequency !== 'ONE_TIME')
  )
]
